/**
 * Záložky Cenníka a ich filtre — spoločné pre stránku, bočný panel
 * a tabuľku (KOLO 166, rozdelenie `CennikPage`).
 *
 * Kam položka patrí, rozhoduje backend (`services/cennik_sekcie`) —
 * záložka posiela len `sekcia`, zoznam aj fasety dostanú tú istú.
 */

import type { HromadnaZmenaResult } from "@/api/hooks/useCennik";
import type { CennikZoraditPole } from "@/api/queryKeys";
import { formatPocet, sklonuj } from "@/lib/format";

/** Hodnota „bez filtra" v rozbaľovačkách (Radix Select nevie prázdnu). */
export const ANY = "__any__";

export type CennikTab = "materialy" | "hrany" | "kovanie" | "operacie" | "tovar";

/** Záložky s položkami cenníka (Operácie majú vlastnú sekciu). */
export type MaterialovyTab = Exclude<CennikTab, "operacie">;

export const TAB_LABELS: Record<CennikTab, string> = {
  materialy: "Materiály",
  hrany: "Hrany",
  kovanie: "Kovanie",
  operacie: "Operácie",
  // Tovar (KOLO 230) — regály CRC, cenník obchodu mimo výroby
  tovar: "Tovar",
};

/** Pod-filter druhu v tabe Materiály. */
export type DruhFilter = "vsetky" | "plosny" | "dlzkovy";

/** Tvary podstatného mena v hlavičke: 1 / 2–4 / 5+ (`sklonuj`). */
export const PODSTATNE_MENO: Record<
  MaterialovyTab,
  readonly [string, string, string]
> = {
  materialy: ["materiál", "materiály", "materiálov"],
  hrany: ["hrana", "hrany", "hrán"],
  kovanie: ["kovanie", "kovania", "kovaní"],
  tovar: ["položka", "položky", "položiek"],
};

/**
 * Filtre Cenníka — jeden objekt, nech sa dajú nulovať naraz. Od KOLA 199
 * (okno B) sú rozbaľovačky v riadku nad tabuľkou (`CennikFiltre`) a pohľady
 * Nedokončené / Nezaradené / Skontroluj cenu v ľavom menu (`CennikMenu`).
 */
export type CennikSidebarFiltre = {
  druhFilter: DruhFilter;
  kategoria: string;
  dodavatel: string;
  vyrobca: string;
  bezStruktury: boolean;
  /**
   * „Nedokončené" = bez kategórie alebo bez kódu.
   *
   * Do KOLA 93 taká položka v cenníku nebola vidieť vôbec (SQL filter
   * kategórií zhltol prázdnu hodnotu), takže sa nedala ani opraviť.
   */
  nedokoncene: boolean;
  /**
   * Len nezaradené — bez kategórie alebo s predvolenou importu
   * (`_DEFAULT_*`, `DEFAULT`). Pohľad na hromadné zaradenie (KOLO 166);
   * počet na čipe je z faset (`nezaradene`).
   */
  nezaradene: boolean;
  /**
   * Len vyradené položky — s tlačidlom Obnoviť (KOLO 166). Platí pre
   * celý cenník ako hľadanie: zmena záložky ho nenuluje, počty na
   * záložkách vtedy rátajú vyradené.
   */
  lenVyradene: boolean;
  /**
   * Len položky, ktoré ten istý výrobok (kód výrobcu) majú aj u iného
   * dodávateľa — štítok „aj u: …" (KOLO 166); počet z faset (`duplicity`).
   */
  lenDuplicity: boolean;
  /**
   * Len položky s odhadnutou nákupnou cenou — štítok „skontroluj cenu"
   * (KOLO 197, upozornenie nad tabuľkou); počet z faset (`skontroluj_cenu`).
   */
  skontrolujCenu: boolean;
};

/** Sekcia z adresy `?sekcia=` (KOLO 199) — neznáma = Materiály. */
export function sekciaZAdresy(hodnota: string | null): CennikTab {
  return (Object.keys(TAB_LABELS) as CennikTab[]).find((t) => t === hodnota) ?? "materialy";
}

/**
 * Čo je vpravo od menu Cenníka: cenník, marže kategórií alebo štruktúry
 * (`?pohlad=`, KOLO 170). Od KOLA 199 sa prepínajú v ľavom menu.
 */
export const POHLADY_STRANKY = ["cennik", "nastavenia-cien", "struktury"] as const;
export type PohladStranky = (typeof POHLADY_STRANKY)[number];

export function pohladZAdresy(hodnota: string | null): PohladStranky {
  return POHLADY_STRANKY.find((p) => p === hodnota) ?? "cennik";
}

export const PRAZDNE_FILTRE: CennikSidebarFiltre = {
  druhFilter: "vsetky",
  kategoria: ANY,
  dodavatel: ANY,
  vyrobca: ANY,
  bezStruktury: false,
  nedokoncene: false,
  nezaradene: false,
  lenVyradene: false,
  lenDuplicity: false,
  skontrolujCenu: false,
};

/** Triedenie tabuľky podľa stĺpca (KOLO 166); `null` = poradie servera. */
export type Zoradenie = { pole: CennikZoraditPole; smer: "asc" | "desc" };

/** Klik na hlavičku: vzostupne → zostupne → bez triedenia. */
export function dalsieZoradenie(
  aktualne: Zoradenie | null,
  pole: CennikZoraditPole,
): Zoradenie | null {
  if (aktualne?.pole !== pole) return { pole, smer: "asc" };
  return aktualne.smer === "asc" ? { pole, smer: "desc" } : null;
}

/** Veľkosti strany tabuľky — server pustí najviac 500. */
export const VELKOSTI_STRANY = [50, 100, 200, 500] as const;
export type VelkostStrany = (typeof VELKOSTI_STRANY)[number];

/**
 * Výber riadkov na hromadnú úpravu (KOLO 166): zaškrtnuté id (aj naprieč
 * stranami) alebo „všetky podľa filtra" — vtedy server vyberie riadky
 * tým istým filtrom a overí počet.
 */
export type VyberRiadkov =
  | { rezim: "ids"; ids: ReadonlySet<string> }
  | { rezim: "filter" };

export const PRAZDNY_VYBER: VyberRiadkov = { rezim: "ids", ids: new Set() };

/** Strop zaškrtnutých riadkov (server pustí 500) — viac ide cez filter. */
export const MAX_VYBRANYCH = 500;

/**
 * Čo hromadná úprava robí — jedno okno, jedna zmena. `marze` = práca,
 * réžia, nadmiera číslom; `marze_kategorie` = späť na čísla kategórie.
 */
export type HromadnaAkcia =
  | "kategoria"
  | "vyrobca"
  | "marze"
  | "marze_kategorie"
  | "vyradit"
  | "obnovit";

/**
 * Popis výsledku hromadnej úpravy do toastu — čo sa nezmenilo a prečo.
 *
 * - `pocet − zmenene` = položky, ktoré cieľovú hodnotu už mali,
 * - `preskocenych_vlastnych` = pri preradení s maržami ostali vlastné,
 * - `s_vlastnymi_marzami` = po zmene marží ich import ani Nastavenia
 *   cien neprepíšu (dôležité vedieť, kým to user nečaká).
 */
export function popisHromadnej(data: HromadnaZmenaResult): string {
  const vety: string[] = [];
  const nezmenene = data.pocet - data.zmenene;
  if (nezmenene > 0) {
    vety.push(
      `${formatPocet(nezmenene)} ${sklonuj(nezmenene, ["položka už mala", "položky už mali", "položiek už malo"])} túto hodnotu.`,
    );
  }
  if (data.preskocenych_vlastnych > 0) {
    const n = data.preskocenych_vlastnych;
    vety.push(
      `Vlastné marže si ${sklonuj(n, ["nechala", "nechali", "nechalo"])} ${formatPocet(n)} ${sklonuj(n, ["položka", "položky", "položiek"])}.`,
    );
  }
  const vlastne = data.s_vlastnymi_marzami ?? 0;
  if (vlastne > 0) {
    vety.push(
      `${formatPocet(vlastne)} ${sklonuj(vlastne, ["položka má", "položky majú", "položiek má"])} vlastné marže — import cenníka ani Nastavenia cien ich neprepíšu.`,
    );
  }
  return vety.join(" ");
}
